import { BellIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import {
    Avatar,
    Button,
    Popover,
    PopoverContent,
    PopoverTrigger,
    ScrollShadow,
    Spinner
} from '@heroui/react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { updateNotificationViewStatus } from '../../services/api';
import { NotificationDto, NotificationPopoverComponentProps, ProfileDto } from '../../types/types';
import { formatTimestamp } from '../../utils/utilMethods';

const NotificationPopover: React.FC<NotificationPopoverComponentProps> = ({
    notifications,
    chatnotifications,
    unreadCount,
    chatUnreadCount,
    onNotificationsUpdate,
    onChatNotificationsUpdate,
    onFetchNotifications,
    onFetchchatNotifications
}) => {
    const { t } = useTranslation('notifications');
    const navigate = useNavigate();
    const [actionLoading, setActionLoading] = useState<{
        [key: number]: boolean;
    }>({});

    const getInitials = (profile: ProfileDto): string => {
        if (profile.firstName && profile.lastName) {
            return `${profile.firstName.charAt(0)}${profile.lastName.charAt(0)}`.toUpperCase();
        }
        if (profile.firstName) {
            return profile.firstName.charAt(0).toUpperCase();
        }
        if (profile.email) {
            return profile.email.charAt(0).toUpperCase();
        }
        return 'U';
    };

    const sanitizeHtml = (html: string): string => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const dangerousElements = ['script', 'iframe', 'object', 'embed', 'form'];
        dangerousElements.forEach(tag => {
            const elements = tempDiv.querySelectorAll(tag);
            elements.forEach(el => el.remove());
        });

        const dangerousAttributes = ['onload', 'onerror', 'onclick', 'onmouseover', 'onfocus', 'onblur'];
        const allElements = tempDiv.querySelectorAll('*');
        allElements.forEach(el => {
            dangerousAttributes.forEach(attr => {
                if (el.hasAttribute(attr)) {
                    el.removeAttribute(attr);
                }
            });
        });

        return tempDiv.innerHTML;
    };

    const createNotificationHtml = (message: string, fromName: string): string => {
        const sanitizedMessage = sanitizeHtml(message);
        return `<span style="font-weight: bold;">${fromName}</span> ${sanitizedMessage}`;
    };

    const handleToggleReadStatus = async (notificationId: number, currentStatus: boolean) => {
        try {
            setActionLoading(prev => ({ ...prev, [notificationId]: true }));
            const updatedNotifications = notifications.map(notification =>
                notification.id === notificationId
                    ? { ...notification, viewStatus: !currentStatus }
                    : notification
            );

            onNotificationsUpdate(updatedNotifications);
            await updateNotificationViewStatus(notificationId);

        } catch (error) {
            const revertedNotifications = notifications.map(notification =>
                notification.id === notificationId
                    ? { ...notification, viewStatus: currentStatus }
                    : notification
            );
            onNotificationsUpdate(revertedNotifications);

        } finally {
            setActionLoading(prev => ({ ...prev, [notificationId]: false }));
        }
    };

    const handleToggleChatReadStatus = async (notificationId: number, currentStatus: boolean) => {
        try {
            setActionLoading(prev => ({ ...prev, [notificationId]: true }));
            const updatedChatNotifications = chatnotifications.map(notification =>
                notification.id === notificationId
                    ? { ...notification, viewStatus: !currentStatus }
                    : notification
            );

            onChatNotificationsUpdate(updatedChatNotifications);
            await updateNotificationViewStatus(notificationId);

        } catch (error) {
            const revertedChatNotifications = chatnotifications.map(notification =>
                notification.id === notificationId
                    ? { ...notification, viewStatus: currentStatus }
                    : notification
            );
            onChatNotificationsUpdate(revertedChatNotifications);

        } finally {
            setActionLoading(prev => ({ ...prev, [notificationId]: false }));
        }
    };

    const handleProfileImageClick = (
        event: React.MouseEvent<HTMLButtonElement>,
        profileId?: string | number | null
    ) => {
        event.stopPropagation();
        if (!profileId) return;
        navigate(`/profile?profileId=${profileId}`);
    };

    const EmptyStateIcon = () => (
        <svg
            className="w-10 h-10 mx-auto text-default-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15 17h5l-5 5v-5z"
            />
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12l2 2 4-4"
            />
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M21 12c0 4.418-4.03 8-9 8s-9-3.582-9-8 4.03-8 9-3.582 9 8z"
            />
        </svg>
    );

    return (
        <div className="flex items-center space-x-2">

            <Popover
                placement="bottom-end"
                showArrow={true}
                offset={10}
                crossOffset={0}
                onOpenChange={(open) => {
                    if (open) {
                        onFetchchatNotifications();
                    }
                }}
            >
                <PopoverTrigger>
                    <Button
                        isIconOnly
                        variant="light"
                        className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        aria-label={t('notifications_aria_label')}
                    >
                        <EnvelopeIcon className="h-5 w-5" />
                        {chatUnreadCount > 0 && (
                            <span className="absolute top-0.5 right-0.5 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                                {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
                            </span>
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0">
                    <div className="flex flex-col">
                        <div className="flex flex-col gap-2 px-4 py-3 bg-white border-b border-divider">
                            <div className="flex items-center justify-between w-full">
                                <h3 className="text-base font-semibold text-foreground">{t('chatnotifications_title')}</h3>
                            </div>
                        </div>
                        <div className="max-h-80 overflow-hidden">
                            <ScrollShadow className="max-h-80">
                                {chatnotifications.length === 0 ? (
                                    <div className="p-6 text-center flex flex-col items-center gap-2">
                                        <EmptyStateIcon />
                                        <div className="text-sm font-medium text-default-500">
                                            {t('no_notifications')}
                                        </div>
                                        <div className="text-xs text-default-400">
                                            {t('no_notifications_description')}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="divide-y divide-divider">
                                        {chatnotifications.map((notification: NotificationDto) => (
                                            <div
                                                key={notification.id}
                                                className={`p-3 hover:bg-default-50 cursor-pointer duration-200 ${!notification.viewStatus
                                                    ? 'bg-primary-50 border-l-4 border-l-primary'
                                                    : ''
                                                    }`}
                                                onClick={() => handleToggleChatReadStatus(notification.id, notification.viewStatus)}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <button
                                                        type="button"
                                                        onClick={(event) =>
                                                            handleProfileImageClick(
                                                                event,
                                                                notification.from.id
                                                            )
                                                        }
                                                        className="flex-shrink-0 rounded-full outline-none focus:outline-none focus:ring-0"
                                                        aria-label={`View ${notification.from.firstName} ${notification.from.lastName} profile`}
                                                    >
                                                        <Avatar
                                                            size="sm"
                                                            src={notification.from.profileImage}
                                                            name={getInitials(notification.from)}
                                                            className="cursor-pointer"
                                                            imgProps={{
                                                                referrerPolicy: "no-referrer",
                                                                crossOrigin: "anonymous"
                                                            }}
                                                        />
                                                    </button>

                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-start justify-between gap-2">
                                                            <div className="flex-1">
                                                                <div
                                                                    className={`text-sm leading-relaxed text-left ${!notification.viewStatus
                                                                        ? 'font-medium text-foreground'
                                                                        : 'text-default-700'
                                                                        }`}
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: createNotificationHtml(
                                                                            notification.message,
                                                                            `${notification.from.firstName} ${notification.from.lastName}`
                                                                        )
                                                                    }}
                                                                    style={{

                                                                        wordBreak: 'break-word',
                                                                        overflowWrap: 'break-word'
                                                                    }}
                                                                />

                                                                <div className="flex items-center mt-1.5 gap-2">
                                                                    <span className="text-xs text-default-500">
                                                                        {formatTimestamp(notification.time)}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            {!notification.viewStatus && (
                                                                <div className="w-1.5 h-1.5 bg-primary rounded-full flex-shrink-0 mt-1.5" />
                                                            )}
                                                        </div>
                                                    </div>

                                                    {actionLoading[notification.id] && (
                                                        <Spinner size="sm" className="flex-shrink-0" />
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </ScrollShadow>
                        </div>
                    </div>
                </PopoverContent>
            </Popover>

            <Popover
                placement="bottom-end"
                showArrow={true}
                offset={10}
                crossOffset={0}
                onOpenChange={(open) => {
                    if (open) {
                        onFetchNotifications();
                    }
                }}
            >
                <PopoverTrigger>
                    <Button
                        isIconOnly
                        variant="light"
                        className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        aria-label={t('notifications_aria_label')}
                    >
                        <BellIcon className="h-5 w-5" />
                        {unreadCount > 0 && (
                            <span className="absolute top-0.5 right-0.5 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                                {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                        )}
                    </Button>
                </PopoverTrigger>

                <PopoverContent className="w-80 p-0">
                    <div className="flex flex-col">
                        <div className="flex flex-col gap-2 px-4 py-3 bg-white border-b border-divider">
                            <div className="flex items-center justify-between w-full">
                                <h3 className="text-base font-semibold text-foreground">{t('notifications_title')}</h3>
                            </div>
                        </div>
                        <div className="max-h-80 overflow-hidden">
                            <ScrollShadow className="max-h-80">
                                {notifications.length === 0 ? (
                                    <div className="p-6 text-center flex flex-col items-center gap-2">
                                        <EmptyStateIcon />
                                        <div className="text-sm font-medium text-default-500">
                                            {t('no_notifications')}
                                        </div>
                                        <div className="text-xs text-default-400">
                                            {t('no_notifications_description')}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="divide-y divide-divider">
                                        {notifications.map((notification: NotificationDto) => (
                                            <div
                                                key={notification.id}
                                                className={`p-3 hover:bg-default-50 cursor-pointer duration-200 ${!notification.viewStatus
                                                    ? 'bg-primary-50 border-l-4 border-l-primary'
                                                    : ''
                                                    }`}
                                                onClick={() => handleToggleReadStatus(notification.id, notification.viewStatus)}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <Avatar
                                                        size="sm"
                                                        src={notification.from.profileImage}
                                                        name={getInitials(notification.from)}
                                                        className="flex-shrink-0"
                                                        imgProps={{
                                                            referrerPolicy: "no-referrer",
                                                            crossOrigin: "anonymous"
                                                        }}
                                                    />

                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-start justify-between gap-2">
                                                            <div className="flex-1">
                                                                <div
                                                                    className={`text-sm leading-relaxed text-left ${!notification.viewStatus
                                                                        ? 'font-medium text-foreground'
                                                                        : 'text-default-700'
                                                                        }`}
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: createNotificationHtml(
                                                                            notification.message,
                                                                            `${notification.from.firstName} ${notification.from.lastName}`
                                                                        )
                                                                    }}
                                                                    style={{
                                                                        wordBreak: 'break-word',
                                                                        overflowWrap: 'break-word'
                                                                    }}
                                                                />

                                                                <div className="flex items-center mt-1.5 gap-2">
                                                                    <span className="text-xs text-default-500">
                                                                        {formatTimestamp(notification.time)}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            {!notification.viewStatus && (
                                                                <div className="w-1.5 h-1.5 bg-primary rounded-full flex-shrink-0 mt-1.5" />
                                                            )}
                                                        </div>
                                                    </div>

                                                    {actionLoading[notification.id] && (
                                                        <Spinner size="sm" className="flex-shrink-0" />
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </ScrollShadow>
                        </div>
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
};

export default NotificationPopover;
